/** Freeze source: the granular worklet fed with the sample's channel data, pitched by pitchRatio = hz / rootHz. */
import type { Drone } from '@tonist/shared';
import { glideTo, radiusGrainSize, normaliseVelocity, targetTau } from './params';
import type { GestureState } from './types';
import { effectiveHz, type SourceUnit, type UnitConfig } from './units';

export interface FreezeSample {
  buffer: AudioBuffer;
  channels: Float32Array[];
  sampleRate: number;
  /** frames */
  loopStart: number;
  loopEnd: number;
  rootHz: number;
}

type FreezeLayer = Extract<Drone['sources'][number], { kind: 'freeze' }>;

export class FreezeUnit implements SourceUnit {
  readonly output: GainNode;
  private readonly node: AudioWorkletNode;
  private readonly baseSizeMs: number;
  private readonly baseDensity: number;

  constructor(
    private readonly cfg: UnitConfig,
    private readonly sample: FreezeSample,
    layer: FreezeLayer,
    densityScale: number,
    private readonly mapping: Drone['gesture'],
  ) {
    const ctx = cfg.ctx;
    this.node = new AudioWorkletNode(ctx, 'tonist-granular', {
      numberOfInputs: 0,
      numberOfOutputs: 1,
      outputChannelCount: [2],
      processorOptions: { seed: cfg.seed },
    });
    const copies = sample.channels.map((c) => c.slice());
    this.node.port.postMessage(
      { type: 'sample', channels: copies, sampleRate: sample.sampleRate, loopStart: sample.loopStart, loopEnd: sample.loopEnd, rootHz: sample.rootHz },
      copies.map((c) => c.buffer),
    );
    this.baseSizeMs = layer.grain.sizeMs;
    this.baseDensity = layer.grain.density * densityScale;
    const region = Math.max(1, sample.loopEnd - sample.loopStart);
    const scatter = (cfg.seed % 1000) / 1000;
    this.param('position').value = Math.min(1, Math.max(0, layer.position + cfg.phaseScatter * scatter * 0.2 * (region > 0 ? 1 : 0)));
    this.param('sizeMs').value = this.baseSizeMs;
    this.param('density').value = this.baseDensity;
    this.param('spraySec').value = layer.grain.spraySec;
    this.param('pitchJitter').value = layer.grain.pitchJitterCents;
    this.param('texture').value = layer.grain.texture;
    this.param('stretch').value = layer.stretch;
    this.output = ctx.createGain();
    this.output.gain.value = cfg.level;
    this.node.connect(this.output);
  }

  private param(name: string): AudioParam {
    const p = this.node.parameters.get(name);
    if (!p) throw new Error(`granular worklet lacks param ${name}`);
    return p;
  }

  start(t: number, hz: number): void {
    this.param('pitchRatio').setValueAtTime(effectiveHz(hz, this.cfg.cents) / this.sample.rootHz, t);
  }

  setPitch(hz: number, glideSec: number, t: number): void {
    glideTo(this.param('pitchRatio'), effectiveHz(hz, this.cfg.cents) / this.sample.rootHz, t, glideSec);
  }

  attachDetuneModulator(node: AudioNode): void {
    node.connect(this.param('detune'));
  }

  setGesture(g: GestureState, t: number): void {
    if (this.mapping.radiusTo === 'grainSize') targetTau(this.param('sizeMs'), this.baseSizeMs * radiusGrainSize(g.radius), t, 0.08);
    if (this.mapping.velocityTo === 'density') targetTau(this.param('density'), this.baseDensity * (0.5 + 2.5 * normaliseVelocity(g.velocity)), t, 0.15);
  }

  /** Live editor updates. */
  update(layer: FreezeLayer, densityScale: number, t: number): void {
    targetTau(this.param('spraySec'), layer.grain.spraySec, t, 0.1);
    targetTau(this.param('pitchJitter'), layer.grain.pitchJitterCents, t, 0.1);
    targetTau(this.param('texture'), layer.grain.texture, t, 0.1);
    targetTau(this.param('stretch'), layer.stretch, t, 0.1);
    targetTau(this.param('sizeMs'), layer.grain.sizeMs, t, 0.1);
    targetTau(this.param('density'), layer.grain.density * densityScale, t, 0.1);
  }

  stop(t: number): void {
    const ms = Math.max(0, (t - this.cfg.ctx.currentTime) * 1000) + 50;
    setTimeout(() => this.dispose(), ms);
  }

  dispose(): void {
    try { this.node.port.postMessage({ type: 'stop' }); } catch { /* gone */ }
    try { this.node.disconnect(); this.output.disconnect(); } catch { /* gone */ }
  }
}
